import React, { useContext, useMemo, useState } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router';
import { useData } from 'resift';
import { Theme } from 'styles/ThemeTyping';
import { SourceType } from 'types/Source';

import makeValidateMappings from 'fetches/makeValidateMappings';
import getSourceHeaderCopy from 'components/Mappings/_helpers/getSourceHeaderCopy';
import MappingProps, * as MappingTypes from 'types/Mapping';
import MappingsContext from 'helpers/MappingsContext';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import Button from '@material-ui/core/Button';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import TableBody from '@material-ui/core/TableBody';
import MappingsRow from 'components/Mappings/MappingsRow';
import PositionIdMapping from './PositionIdMapping';
import AccordionSectionHeader from 'molecules/AccordionSectionHeader';
import AccordionSectionContent from 'molecules/AccordionSectionContent';
import AccordionSectionButtonContainer from 'molecules/AccordionSectionButtonContainer';
import OverallValidationStatus from 'components/Mappings/OverallValidationStatus';
import {
  MAP_ATTRIBUTES_URL,
  POSITION_ID_MAPPING_TYPE_KEY,
  CLIENT_PRIMARY_KEY,
  CLIENT_PERSON_ID_KEY,
  PRIMARY_MAPPING_NOT_SET,
} from 'pages/Directory/consts';

interface Props {
  mappings: MappingProps[];
  isExpanded: boolean;
  onDeleteMapping: (indexToDelete: number) => void;
  onUpdateMapping: (newMappingIndex: number, newMappings: MappingProps[]) => void;
  onNextStep: () => void;
  onToggleExpansion: () => void;
}

function PrimaryKeyMapping({
  mappings,
  isExpanded,
  onDeleteMapping,
  onUpdateMapping,
  onNextStep,
  onToggleExpansion,
}: Props) {
  const classes = useStyles();
  const mappingContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const history = useHistory();
  const showPositionIdInfoInitialState = mappings.some(
    mapping => mapping.type === POSITION_ID_MAPPING_TYPE_KEY,
  );
  const [showPositionIdInfo, setShowPositionIdInfo] = useState(showPositionIdInfoInitialState);
  const onSourceCreationWizardMappings = history.location.pathname.includes(MAP_ATTRIBUTES_URL);
  const { source } = mappingContext;
  const isCsvSource = source.type === SourceType.CSV;
  const validateMappings = makeValidateMappings(source.id);
  const validationResponse = useData(validateMappings);
  const validationResults = _get(validationResponse, 'validationResults');
  const { isPrimary, type } = source;

  const ValidationStatus = useMemo(() => {
    const otherOrgMappingIssues = !_isEmpty(validationResults)
      ? validationResults.filter(
          (vr: MappingTypes.MappingValidationResult) => vr.code === PRIMARY_MAPPING_NOT_SET,
        )
      : undefined;

    return (
      <OverallValidationStatus
        mappings={mappings}
        errorsNotTiedToSpecificMappingRows={otherOrgMappingIssues}
      />
    );
  }, [mappings]);

  const renderCardHeader = () => {
    return (
      <CardHeader
        title={`${
          onSourceCreationWizardMappings
            ? 'Step 1: Assign the Unique Identifier'
            : 'Assign the Unique Identifier'
        }`}
        classes={{ action: classes.action }}
        action={ValidationStatus}
        className={classes.cardHeader}
      />
    );
  };

  const handleOpenPositionIdSection = () => {
    setShowPositionIdInfo(true);

    const newMapping = {
      source: '',
      destination: CLIENT_PERSON_ID_KEY,
      isRequired: false,
      isUnique: false,
      type: POSITION_ID_MAPPING_TYPE_KEY,
    };

    const newMappings = [...mappings, newMapping];
    onUpdateMapping(0, newMappings);
  };

  const handleClosePositionIdSection = () => {
    setShowPositionIdInfo(false);
  };

  const renderDetails = () => {
    return (
      <CardContent className={classes.cardContent}>
        <div className={classes.helpText}>
          {isPrimary ? (
            <Typography variant="body2">
              The Unique Identifier defines a person or position’s identity in Sift. It should be
              something that is unique to each imported person, and should ideally never change. We
              recommend something like an <strong>employee id</strong> or{' '}
              <strong>position id</strong>, but if that’s not available you could also use something
              like an email address.
            </Typography>
          ) : (
            <Typography variant="body2">
              The Unique Identifier defines a person’s identity in Sift and is used to connect data
              from this data source to the profiles created from your Primary source. The values in
              this property should match the associated unique identifiers provided in the primary
              source.
            </Typography>
          )}

          <Typography className={classes.helpTextNote} variant="body2">
            <strong>Note:</strong> This cannot be changed after your first sync without contacting
            our support team.
          </Typography>
        </div>
        <Table>
          <TableHead>
            <TableHeadCell>{getSourceHeaderCopy(type)}</TableHeadCell>
            <TableHeadCell>Preview</TableHeadCell>
            <TableHeadCell colSpan={2} />
          </TableHead>
          <TableBody>
            {mappings
              .filter(mapping => mapping.destination === CLIENT_PRIMARY_KEY)
              .map((mapping: MappingProps, index: number) => {
                return (
                  <MappingsRow
                    key={index}
                    mapping={mapping}
                    rowIndex={index}
                    onDeleteMapping={onDeleteMapping}
                    onUpdateMapping={onUpdateMapping}
                  />
                );
              })}
          </TableBody>
        </Table>
        {isCsvSource && (
          <Button
            onClick={handleOpenPositionIdSection}
            className={classes.positionIdButton}
            color="secondary"
            disabled={mappings.some(mapping => mapping.type === POSITION_ID_MAPPING_TYPE_KEY)}
          >
            Advanced: My file contains people who have multiple positions
          </Button>
        )}

        {isCsvSource && showPositionIdInfo && (
          <PositionIdMapping
            mappings={mappings.filter(mapping => mapping.destination === CLIENT_PERSON_ID_KEY)}
            mappingIndex={1}
            onDeleteMapping={onDeleteMapping}
            onUpdateMapping={onUpdateMapping}
            onClose={handleClosePositionIdSection}
          />
        )}
      </CardContent>
    );
  };

  return (
    <>
      {!onSourceCreationWizardMappings ? (
        <>
          {renderCardHeader()}
          {renderDetails()}
        </>
      ) : (
        <Accordion expanded={isExpanded} onChange={onToggleExpansion} className={classes.accordion}>
          <AccordionSectionHeader>{renderCardHeader()}</AccordionSectionHeader>
          <AccordionSectionContent>
            {renderDetails()}
            <AccordionSectionButtonContainer>
              <Button variant="contained" color="secondary" onClick={onNextStep}>
                Next Step
              </Button>
            </AccordionSectionButtonContainer>
          </AccordionSectionContent>
        </Accordion>
      )}
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  cardHeader: {
    width: '100%',
  },
  helpText: {
    marginBottom: theme.spacing(),
  },
  helpTextNote: {
    marginTop: theme.spacing(3),
  },
  accordion: {
    padding: 0,
    margin: 0,
  },
  cardContent: {
    paddingTop: 0,
  },
  action: {
    margin: 0,
    alignSelf: 'center',
  },
  positionIdButton: {
    margin: theme.spacing(2),
    marginLeft: 0,
    fontWeight: theme.typography.fontWeight.semiBold,
    '&:disabled': {
      color: theme.palette.secondary.main,
    },
  },
}));

export default PrimaryKeyMapping;
